import ComparisonReceiptValue from './components/calculation-snapshot-receipt-value'

const CUSTOMS_ROWS = [
  { field: 'lowin_total', labelKey: 'calc__text__customs__customsCost' },
  { field: 'customs_duty_total', labelKey: 'common__text__customsPayments' },
  { field: 'customs_vat_total', labelKey: 'calc__text__customs__vatAmount' },
  { field: 'customs_excise_total', labelKey: 'calc__text__customs__excise' },
  { field: 'customs_utilization_total', labelKey: 'calc__text__customs__utilizationFee' },
  { field: 'customs_environmental_total', labelKey: 'calc__text__customs__environmentalFee' },
  { field: 'customs_tax_total', labelKey: 'calc__text__customs__customsDutyCapitalLetter' },
  { field: 'broker_total', labelKey: 'applications__text__proposalServiceLine_customs_broker' },
  { field: 'customs_step_total', labelKey: 'common__text__inTotal' },
] as const

const TOTAL_ROWS = [
  { field: 'total_amount', labelKey: 'applications__text__calcSummaryTotalUsdAllIn' },
  { field: 'net_total_amount', labelKey: 'applications__text__calcSummaryTotalUsdExclVat' },
  { field: 'vat_total', labelKey: 'calc__text__customs__vatAmount' },
] as const

export function hasComparisonAmount(comparison?: AmountComparison | null): boolean {
  return Boolean(comparison?.after?.trim() || comparison?.before?.trim())
}

export function getApplicationComparisonPresentation({
  calculationComparison,
  calculationCurrencyCode,
  hasProposalAdjustment,
  bankCommissions,
  translate: t,
}: ApplicationComparisonPresentationArgs): Pick<
  SummaryCalculatorData,
  'transformInfo' | 'totalHeading' | 'headerTotal'
> {
  if (!calculationComparison) {
    return {}
  }

  const currencyCode = calculationCurrencyCode || 'USD'
  const payments = bankCommissions?.invoices?.flatMap((invoice) => invoice.banking_commissions ?? []) ?? []
  const singlePayment = payments.length === 1
  const transformInfo = ({ section, info }: SummaryCalculatorTransformInfoArgs) => {
    const replacements: { field: CalculationComparisonField; label: string; displayLabel?: string }[] = []
    if (section === 'customs') {
      replacements.push(...CUSTOMS_ROWS.map(({ field, labelKey }) => ({ field, label: t(labelKey) })))
    } else if (section === 'delivery') {
      replacements.push({ field: 'shipg_total', label: t('calc__text__rightPanel__preliminaryCostOfDelivery') })
    } else if (section === 'certification') {
      replacements.push({ field: 'cert_total', label: t('common__text__cost') })
    } else if (section === 'total') {
      replacements.push(...TOTAL_ROWS.map(({ field, labelKey }) => ({ field, label: t(labelKey) })))
    } else if (section === 'bank_commissions') {
      if (singlePayment) {
        replacements.push({
          field: 'total_payment_amount',
          label: `${t('calc__text__bankCommissions__payment')} 1 (${payments[0].commission_percentage}%)`,
          displayLabel: hasProposalAdjustment ? t('applications__text__proposalServiceLine_payment_agent') : undefined,
        })
      } else if (!payments.length) {
        replacements.push({ field: 'total_payment_amount', label: t('applications__text__proposalServiceLine_payment_agent') })
      }
      if (singlePayment || !payments.length) {
        replacements.push(
          { field: 'total_banking_commissions', label: t('calc__text__bankCommissions__bankCommissionForTheTransfer') },
          { field: 'total_currency_control_fees', label: t('calc__text__bankCommissions__currencyControlCommission') },
        )
      }
      replacements.push({ field: 'bank_total_to_pay', label: t('calc__text__bankCommissions__totalToBePaid') })
    }
    const available = replacements.filter(({ field }) => hasComparisonAmount(calculationComparison[field]))
    const shouldRenamePayment = section === 'bank_commissions' && singlePayment && hasProposalAdjustment
    if (!available.length && !shouldRenamePayment && !(section === 'total' && hasProposalAdjustment)) {
      return info
    }

    const replacedLabels = new Set<string>()
    const result = info.map((group) => group.map((row) => {
      const replacement = replacements.find(({ label }) => label === row.label)
      if (!replacement) {
        const isOriginalRubAmount = section === 'total' && hasProposalAdjustment && [
          t('applications__text__calcSummaryRubExclVat'),
          t('applications__text__calcSummaryRubInclVat'),
        ].includes(row.label)
        return isOriginalRubAmount ? {
          ...row,
          label: `${row.label}\n(${t('calc__text__summary__theInitialCost')})`,
        } : row
      }
      replacedLabels.add(row.label)
      if (!hasComparisonAmount(calculationComparison[replacement.field])) {
        return replacement.displayLabel ? { ...row, label: replacement.displayLabel } : row
      }
      return {
        ...row,
        label: replacement.displayLabel || row.label,
        value: (
          <ComparisonReceiptValue
            comparison={calculationComparison[replacement.field]}
            currencyCode={currencyCode}
          />
        ),
      }
    }))
    const missing = available.filter(({ label }) => !replacedLabels.has(label))
    if (missing.length) {
      result.push(missing.map(({ field, label, displayLabel }) => ({
        label: displayLabel || label,
        value: (
          <ComparisonReceiptValue
            comparison={calculationComparison[field]}
            currencyCode={currencyCode}
          />
        ),
      })))
    }
    return result
  }

  const hasTotalComparison = TOTAL_ROWS.some(({ field }) => hasComparisonAmount(calculationComparison[field]))
  return {
    transformInfo,
    ...(hasTotalComparison ? {
      totalHeading: true,
    } : {}),
    ...(hasComparisonAmount(calculationComparison.total_amount) ? {
      headerTotal: (
        <ComparisonReceiptValue
          alignment='left'
          comparison={calculationComparison.total_amount}
          currencyCode={currencyCode}
        />
      ),
    } : {}),
  }
}
